<template>

  <shell :title="$t('titles.reporting')">

    <v-row class="mb-4 elevation-4 bg-background" style="position: sticky; top: 0; z-index: 999;">

      <v-col v-if="canManageOrganizations" cols="12">

        <o-autocomplete
          :label="$t('titles.organization')"
          :items="Organization.getOptions()"
          v-model="formState.organizationId"
          clearable
        />

      </v-col>

      <v-col cols="12" md="6" lg="3" xl="4">

        <o-autocomplete
          :label="$t('titles.locations')"
          multiple
          :items="locationOptions"
          :validator="v$.locationIds"
          v-model="pendingFormState.locationIds"
          clearable
          @blur="() => formState.locationIds = pendingFormState.locationIds"
        >

          <template v-slot:selection="{ item, index }">
            <span class="v-autocomplete__selection-text" v-if="index < 2">
              <template v-if="index === 0">
                {{ item.title }}<span v-if="pendingFormState.locationIds.length > 1" class="v-autocomplete__selection-comma">,</span>
              </template>
              <template v-if="index === 1">
                + {{ pendingFormState.locationIds.length - 1 }} more
              </template>
            </span>
          </template>

          <template v-slot:prepend-item>
            <v-list-item title="Select All" @click="toggleAllLocations">
              <template v-slot:prepend>
                <v-checkbox-btn
                  :indeterminate="someLocationsSelected && !allLocationsSelected"
                  :model-value="allLocationsSelected"
                />
              </template>
            </v-list-item>

            <v-divider class="mt-2"></v-divider>
          </template>

        </o-autocomplete>

      </v-col>

      <v-col cols="12" md="6" lg="3" xl="4">

        <o-autocomplete
          :label="$t('titles.channels')"
          multiple
          :items="channelOptions"
          v-model="pendingFormState.channels"
          clearable
          :disabled="route.name === 'breakdownsReport'"
          @blur="() => formState.channels = pendingFormState.channels"
          :loading="loadingChannelsByLocationID"
        />

      </v-col>

      <v-col cols="12" lg="6" xl="4" class="text-end">

        <v-btn-toggle
          v-model="formState.interval"
          group
          divided
          density="compact"
          variant="outlined"
          mandatory
          :disabled="route.name === 'activityReport'"
        >
          <v-btn :value="1">
            {{ $t('titles.today') }}
          </v-btn>
          <v-btn :value="7">
            <v-tooltip activator="parent" :text="$t('titles.last_n_days', {n: 7})" location="bottom" />
            7
          </v-btn>
          <v-btn :value="14">
            <v-tooltip activator="parent" :text="$t('titles.last_n_days', {n: 14})" location="bottom" />
            14
          </v-btn>
          <v-btn :value="30">
            <v-tooltip activator="parent" :text="$t('titles.last_n_days', {n: 30})" location="bottom" />
            30
          </v-btn>
          <v-btn :value="60">
            <v-tooltip activator="parent" :text="$t('titles.last_n_days', {n: 60})" location="bottom" />
            60
          </v-btn>
          <v-btn :value="90">
            <v-tooltip activator="parent" :text="$t('titles.last_n_days', {n: 90})" location="bottom" />
            90
          </v-btn>
          <v-btn :value="'ytd'">
            <v-tooltip activator="parent" :text="$t('titles.year_to_date')" location="bottom" />
            {{ $t('titles.year_to_date_abbr') }}
          </v-btn>
          <v-btn :value="'custom'" id="custom-button">
            {{ $t('titles.custom') }}
          </v-btn>
        </v-btn-toggle>

        <!--
        Nesting this menu inside the button (with activator="parent") caused
        very weird issues - picking dates caused formState.interval to change
        to inexplicable integer values! Possibly a bug in Vuetify?
        -->
        <v-menu location="bottom" activator="#custom-button" :close-on-content-click="false" @update:modelValue="customDateMenuToggled">
          <v-sheet class="px-4">
            <v-container>

              <v-date-picker
                multiple="range"
                hide-header
                v-model="pendingFormState.customDates"
                min="2020-01"
              />

            </v-container>
          </v-sheet>
        </v-menu>

        <div :style="{visibility: formState.interval === 'custom' ? 'visible' : 'hidden'}" class="text-end mt-3">

          <span v-if="v$.customStart.$invalid || v$.customEnd.$invalid" class="text-error">
            Invalid custom date range
          </span>

          <template v-if="!v$.customStart.$invalid && !v$.customEnd.$invalid">
            {{ formState.customStart }}
            <span v-if="formState.customStart !== formState.customEnd">
              – {{ formState.customEnd }}
            </span>
          </template>

        </div>

      </v-col>

    </v-row>

    <v-tabs class="mb-12">
      <v-tab :to="{name: 'overviewReport', query: linkQuery}" :text="t('titles.overview')" />
      <v-tab :to="{name: 'breakdownsReport', query: linkQuery}" :text="t('titles.breakdowns')" />
      <v-tab :to="{name: 'customersReport', query: linkQuery}" :text="t('titles.customers')" />
      <v-tab :to="{name: 'transactionsReport', query: linkQuery}" :text="t('titles.transactions')" />
      <v-tab :to="{name: 'activityReport', query: linkQuery}" :text="t('titles.activity')" />
      <v-tab :to="{name: 'dailyRevenueReport', query: linkQuery}" :text="t('titles.daily_revenue')" />
    </v-tabs>

    <router-view
      :loading="loading"
      :date-summaries="dateSummaries"
      :location-ids="formState.locationIds"
      :channels="formState.channels"
      :start="start"
      :end="end"
    />

  </shell>

</template>

<script setup lang="ts">

import { computed, inject, reactive, ref, watch } from 'vue'
import { computedAsync } from '@vueuse/core'
import { type LocationQueryRaw, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

// components
import Shell from '../Shell.vue'

// models
import Location from '../../../models/Location'
import LocationProduct from '../../../models/LocationProduct'
import Organization from '../../../models/Organization'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers, requiredIf } from '@vuelidate/validators'
import { required } from '../../../utils/i18n-validators'

// misc
import axios from 'axios'
import moment from 'moment'
import { Temporal } from '@js-temporal/polyfill'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import NotificationService from '../../../services/NotificationService'
import { type ReportingDateSummaries, ReportingApi } from '../../../services/api-clients/ReportingApi'
import {
  QuerySyncer,
  AbstractSerializer,
  CommaSeparatedNumberSerializer,
  CommaSeparatedSerializer,
  DateSerializer,
} from '../../../utils/QuerySyncer'
import type { VAutocompleteItem } from '../../../types/vuetify'
import type { Can } from '../../../main'

const $can = inject('$can') as Can
const canManageOrganizations = $can('organizations.manage')
const canSeeReportingForNonLiveLocations = $can('reportingfornonlivelocations.read')

const { t } = useI18n()
const route = useRoute()

/**
 * Form state and rules
 */

interface FormState {
  organizationId: null | number
  locationIds: number[]
  channels: string[]
  interval: number | 'ytd' | 'custom'
  customStart: any // TODO
  customEnd: any // TODO
}

const today = Temporal.Now.plainDateISO()

const formState = reactive<FormState>({
  organizationId: null,
  locationIds: [],
  channels: [],
  interval: 1,
  customStart: today.subtract({days: 30}).toString(),
  customEnd: today.toString(),
})

const formRules = {
  locationIds: {
    required,
    $autoDirty: true,
  },
  customStart: {
    requiredIfCustom: requiredIf(() => formState.interval === 'custom'),
    notAfterEnd: helpers.withMessage(
      t('validations.start_date_not_after_end_date'),
      (value: any) => !helpers.req(value) || value <= formState.customEnd
    ),
    $autoDirty: true,
  },
  customEnd: {
    requiredIfCustom: requiredIf(() => formState.interval === 'custom'),
    notBeforeStart: helpers.withMessage(
      t('validations.end_date_not_before_start_date'),
      (value: any) => !helpers.req(value) || value >= formState.customStart
    ),
    $autoDirty: true,
  },
}

const v$ = useVuelidate(formRules, formState)
// v$.value.$touch() // mark all fields as dirty, so error messages will be displayed

// TODO: explain pendingFormState

interface PendingFormState {
  locationIds: number[]
  channels: string[]
  customDates: any[] // TODO
}

const pendingFormState = reactive<PendingFormState>({
  locationIds: [],
  channels: [],
  customDates: [],
})

/**
 * TODO: explain
 */

const channelsByLocationID = reactive<{[key: number]: Set<string>}>({})
const loadingChannelsByLocationID = ref(false)

watch(() => formState.locationIds, () => {

  // determine which locations (if any) we need to load data for

  const unloadedLocationIds = formState.locationIds.filter((locationId) => {
    return !(locationId in channelsByLocationID)
  })

  if (!unloadedLocationIds.length) {
    return
  }

  // load the data

  loadingChannelsByLocationID.value = true

  const promises = unloadedLocationIds.map((locationId) => {

  LocationProduct.fetchByLocationID(locationId)
    .then(response => {

      if (!response) {
        throw 'hi'
      }

      const channelFacilityIdsWithLpcfs: Set<number> = new Set()

      response.response.data.locationProducts.forEach((lp: any) => {
        lp.channelFacilityIDs.forEach((channelFacilityId: number) => {
          channelFacilityIdsWithLpcfs.add(channelFacilityId)
        })
      })

      const channels: Set<string> = new Set()

      response.response.data.channelFacilities.forEach((cf: any) => {
        if (channelFacilityIdsWithLpcfs.has(cf.ID)) {
          channels.add(cf.channel)
        }
      })

      channelsByLocationID[locationId] = channels

    })

  })

  Promise.all(promises)
    .catch((error) => {
      console.error(error)
      NotificationService.error(`Couldn\'t load channels for one or more locations!`)
    })
    .finally(() => {
      loadingChannelsByLocationID.value = false
    })

})

/**
 * Location field
 */

const locationOptions = computed<VAutocompleteItem[]>(() => {

  const liveOnly = !canSeeReportingForNonLiveLocations

  return Location.getOptions('reporting', 'read', formState.organizationId, liveOnly)
  .sort((option1, option2) => {
    const option1Selected = formState.locationIds.includes(option1.value)
    const option2Selected = formState.locationIds.includes(option2.value)
    if (option1Selected && !option2Selected) {
      return -1
    } else if (!option1Selected && option2Selected) {
      return 1
    }
    return 0
  })
})

// When the location options change, remove any selected values that are no
// longer selectable. (We only update the form objects if the value has
// actually changed, to avoid triggering unnecessary API calls.)
watch(locationOptions, () => {

  const selectableLocationIds = locationOptions.value.map((option) => option.value)

  if (formState.locationIds.some((locationId) => !selectableLocationIds.includes(locationId))) {
    formState.locationIds = formState.locationIds.filter((locationId) => {
      return selectableLocationIds.includes(locationId)
    })
  }

  if (pendingFormState.locationIds.some((locationId) => !selectableLocationIds.includes(locationId))) {
    pendingFormState.locationIds = pendingFormState.locationIds.filter((locationId) => {
      return selectableLocationIds.includes(locationId)
    })
  }

})

const someLocationsSelected = computed(() => {
  return pendingFormState.locationIds.length > 0
})

const allLocationsSelected = computed(() => {
  return pendingFormState.locationIds.length >= locationOptions.value.length
})

function toggleAllLocations() {
  if (allLocationsSelected.value) {
    pendingFormState.locationIds = []
  } else {
    pendingFormState.locationIds = locationOptions.value.map((option) => {
      return option.value
    })
  }
}

/**
 * Channel field
 */

const channelOptions = computed<VAutocompleteItem[]>(() => {

  const channelsForSelectedLocations = new Set<string>()

  formState.locationIds
  .filter((locationId) => locationId in channelsByLocationID)
  .forEach((locationId) => {
    if (locationId in channelsByLocationID) {
      channelsByLocationID[locationId].forEach((channel) => {
        channelsForSelectedLocations.add(channel)
      })
    }
  })

  const options: VAutocompleteItem[] = []

  channelsForSelectedLocations.forEach((channelName) => {
    const channel = ChannelRegistry.find(channelName)
    if (channel) {
      options.push({
        value: channel.name,
        title: channel.titleShort,
      })
    }
  })

  return options

})

/**
 * When the channel options change, remove any selected values that are no
 * longer selectable. (We only update the form objects if the value has
 * actually changed, to avoid triggering unnecessary API calls.)
 */

// bit of a hack
let haveSeenNonEmptyChannelOptions: boolean = false

watch(channelOptions, () => {

  if (channelOptions.value.length > 0) {
    haveSeenNonEmptyChannelOptions = true
  }

  const selectableChannels = channelOptions.value.map((option) => option.value)

  if (formState.channels.some((channel) => !selectableChannels.includes(channel))) {
    formState.channels = formState.channels.filter((channel) => {
      return selectableChannels.includes(channel) || !haveSeenNonEmptyChannelOptions
    })
  }

  if (pendingFormState.channels.some((channel) => !selectableChannels.includes(channel))) {
    pendingFormState.channels = pendingFormState.channels.filter((channel) => {
      return selectableChannels.includes(channel) || !haveSeenNonEmptyChannelOptions
    })
  }

})

/**
 * When the "Custom" menu is closed, copy its values from the pending form to
 * the real form
 */
function customDateMenuToggled(isMenuOpen: boolean) {

  if (isMenuOpen) {
    return
  }

  let newCustomStart = null
  let newCustomEnd = null

  // VDatePicker's model is an array of dates. We need to find the earliest
  // and latest among them.
  if (pendingFormState.customDates.length > 0) {
    const minTimestamp = Math.min.apply(null, pendingFormState.customDates)
    const maxTimestamp = Math.max.apply(null, pendingFormState.customDates)
    newCustomStart = moment(new Date(minTimestamp)).format(moment.HTML5_FMT.DATE)
    newCustomEnd = moment(new Date(maxTimestamp)).format(moment.HTML5_FMT.DATE)
  }

  formState.customStart = newCustomStart
  formState.customEnd = newCustomEnd

}

/**
 * Computed start and end dates (based on the selected interval)
 */

const start = computed<null | Temporal.PlainDate>(() => {
  switch (formState.interval) {
    case 'ytd':
      return today.with({month: 1, day: 1})
      break
    case 'custom':
      return !v$.value.customStart.$invalid ? Temporal.PlainDate.from(formState.customStart) : null // TODO: update child components to handle null values
      break
    default:
      return today.subtract({days: formState.interval - 1})
      break
  }
})

const end = computed<null | Temporal.PlainDate>(() => {

  switch (formState.interval) {
    case 'custom':
      return !v$.value.customEnd.$invalid ? Temporal.PlainDate.from(formState.customEnd) : null // TODO: update child components to handle null values
      break
    default:
      return today
      break
  }
})

/**
 * Sync formState to the querystring (and vice-versa)
 */

class IntervalSerializer extends AbstractSerializer {

  // This value sometimes needs to be deserialized to a Number, and other
  // times to a String.

  static serialize(value: number | 'ytd' | 'custom') {
    if (typeof value === 'string') {
      return value
    } else if (typeof value === 'number') {
      return value.toString()
    } else {
      return null
    }
  }

  static unserialize(value: string) {
    if (value === 'custom') {
      return value
    } else if (/^[1-9][0-9]*$/.test(value)) {
      return parseInt(value, 10) // TODO: ideally, we would only accept values that are actually in the VBtnToggle
    } else {
      return null
    }
  }

}

new QuerySyncer({
  location_ids: {
    watchable: () => formState.locationIds,
    hydrate: (newValue: number[]) => {
      const selectableLocationIds = locationOptions.value.map(o => o.value)
      const selectedLocationIds = newValue.filter((locationId: number) => selectableLocationIds.includes(locationId))
      formState.locationIds = selectedLocationIds
      pendingFormState.locationIds = selectedLocationIds
    },
    serializer: CommaSeparatedNumberSerializer,
  },
  channels: {
    watchable: () => formState.channels,
    hydrate: (newValue) => {
      formState.channels = newValue
      pendingFormState.channels = newValue
    },
    serializer: CommaSeparatedSerializer,
  },
  interval: {
    watchable: () => formState.interval,
    hydrate: (newValue) => formState.interval = newValue,
    serializer: IntervalSerializer,
  },
  start: {
    watchable: () => formState.interval === 'custom' ? formState.customStart : null,
    hydrate: (newValue) => {
      formState.customStart = newValue
      // pendingFormState.customStart = newValue
    },
    serializer: DateSerializer,
  },
  end: {
    watchable: () => formState.interval === 'custom' ? formState.customEnd : null,
    hydrate: (newValue) => {
      formState.customEnd = newValue
      // pendingFormState.customEnd = newValue
    },
    serializer: DateSerializer,
  },
})

/**
 * Preserve these querystring vars when navigating to different tabs
 */
const linkQuery = computed<LocationQueryRaw>(() => {

  const output: LocationQueryRaw = {}

  if (formState.locationIds.length) {
    output.location_ids = formState.locationIds.join(',')
  }

  if (formState.channels.length) {
    output.channels = formState.channels.join(',')
  }

  output.interval = formState.interval

  if (formState.interval === 'custom') {
    output.start = start.value?.toString()
    output.end = end.value?.toString()
  }

  return output

})

/**
 * Raw data to feed to the individual tabs
 */

const loading = ref(false)
const pendingRequestAbortControllers: AbortController[] = []

const dateSummaries = computedAsync<ReportingDateSummaries>(

  async () => {

    if (v$.value.$invalid || !start.value || !end.value) {
      return {}
    }

    pendingRequestAbortControllers.forEach((controller) => {
      controller.abort()
    })
    pendingRequestAbortControllers.length = 0

    const abortController = new AbortController()
    pendingRequestAbortControllers.push(abortController)

    loading.value = true
    let data: ReportingDateSummaries = {}

    await ReportingApi
    .getDateSummaries({
      locationIds: new Set(formState.locationIds),
      start: Temporal.PlainDate.from(start.value),
      end: Temporal.PlainDate.from(end.value),
      signal: abortController.signal,
    })
    .then((resultData) => {
      data = resultData
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        return
      }
      console.error(error)
      NotificationService.error('Couldn\'t load report data!')
    })
    .finally(() => {
      loading.value = false
    })

    return data

  },

  {} // default value

)

</script>
